const BASE = "/api/agent";

// SSE 终止事件：流必须以其中之一结束，否则视为提前断流（AFV-001）
const TERMINAL_EVENTS = new Set(["done", "error", "stream_cancelled"]);

/**
 * 读取 Agent SSE 流并逐事件回调。
 * 连接在收到终止事件（done/error/stream_cancelled）前结束，或出现无法解析的事件块时，
 * 抛出带 code=SSE_INCOMPLETE 的错误，避免断流被当作正常完成（AFV-001）。
 */
async function readAgentSSE(res, onEvent) {
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let terminalReceived = false;
  let malformedCount = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() || "";
    for (const block of events) {
      const line = block.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      try {
        const payload = JSON.parse(line.replace(/^data: /, ""));
        if (TERMINAL_EVENTS.has(payload.event)) terminalReceived = true;
        onEvent(payload);
      } catch {
        // 非法事件不中断读取，但计数；若最终没收到终止事件则一并报告
        malformedCount++;
      }
    }
  }
  if (!terminalReceived) {
    const err = new Error(malformedCount > 0
      ? `连接异常结束且有 ${malformedCount} 个事件无法解析，回复可能不完整`
      : "连接提前中断，回复可能不完整");
    err.code = "SSE_INCOMPLETE";
    throw err;
  }
}

export function postAgentRunSSE({ sessionId, message, attachments, artifactIds = [], clientMessageId = null, onEvent, signal }) {
  const url = `${BASE}/run`;
  const payload = { session_id: sessionId, message, attachments: attachments || [], artifact_ids: artifactIds, client_message_id: clientMessageId };
  return fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
  }).then(async (res) => {
    if (!res.ok) {
      let errData = {};
      try { errData = await res.json(); } catch {}
      const err = new Error(errData.message || `HTTP ${res.status}`);
      err.code = errData.code;
      err.status = res.status;
      throw err;
    }
    await readAgentSSE(res, onEvent);
  });
}

export function regenerateAgentMessageSSE({ sessionId, messageId, onEvent, signal }) {
  const url = `${BASE}/messages/${messageId}/regenerate`;
  return fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
    signal,
  }).then(async (res) => {
    if (!res.ok) {
      let errData = {};
      try { errData = await res.json(); } catch {}
      const err = new Error(errData.message || `HTTP ${res.status}`);
      err.code = errData.code;
      err.status = res.status;
      throw err;
    }
    await readAgentSSE(res, onEvent);
  });
}

export async function submitFeedback({ messageId, feedbackType, note, correctionType = null }) {
  const res = await fetch(`${BASE}/feedback`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message_id: messageId, feedback_type: feedbackType, note, correction_type: correctionType }),
  });
  if (!res.ok) {
    let errData = {};
    try { errData = await res.json(); } catch {}
    throw new Error(errData.message || `HTTP ${res.status}`);
  }
  return res.json();
}

// 获取当前用户某角色下已安装技能列表（工作台技能 tab 使用）
export async function fetchInstalledSkills(roleCode) {
  const url = roleCode ? `${BASE}/skills?roleCode=${encodeURIComponent(roleCode)}` : `${BASE}/skills`;
  const res = await fetch(url, { credentials: "same-origin" });
  if (!res.ok) {
    let errData = {};
    try { errData = await res.json(); } catch {}
    throw new Error(errData.message || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return Array.isArray(data.installed) ? data.installed : [];
}

// 获取 Agent 已装配的工具能力清单（按 category 分组，用于工作台工具分栏展示）
export async function fetchToolCapabilities() {
  const res = await fetch(`${BASE}/tools`, { credentials: "same-origin" });
  if (!res.ok) {
    let errData = {};
    try { errData = await res.json(); } catch {}
    throw new Error(errData.message || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return Array.isArray(data.categories) ? data.categories : [];
}

// D1 受控 Tool 审批框架：提交用户审批结果（确认/拒绝）
// 完整跨线程闭环待业务 Tool 接入时实现；当前为预留骨架
export async function submitApproval({ callId, approved }) {
  const res = await fetch(`${BASE}/approval`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ call_id: callId, approved }),
  });
  if (!res.ok) {
    let errData = {};
    try { errData = await res.json(); } catch {}
    throw new Error(errData.message || `HTTP ${res.status}`);
  }
  return res.json();
}
